// paycor-moove-embed.module.js
// Robust loader: injects Paycor/Newton only after Moove marketing consent,
// then relocates the injected iframe into a target container.
// Comments in English as requested.

const DEFAULTS = {
  bodyClass: 'page-careers',
  containerSelector: '#paycor-embed',

  scriptId: 'gnewtonjs',
  src: '//newton.newtonsoftware.com/career/iframe.action?clientId=XXXXXX',

  // Matching strategy: src might be blank initially, so this is only one signal.
  iframeSrcMatch: 'newtonsoftware.com',

  cookieName: 'moove_gdpr_popup',
  marketingKeys: ['thirdparty', 'advanced'],
  ///{strict, thirdparty, advanced, performance, preference}
  observerTimeoutMs: 20000,
};

function readCookie(name) {
  const match = document.cookie.match(new RegExp(`(?:^|;\\s*)${name}=([^;]+)`));
  return match ? match[1] : null;
}

function getMooveConsent(cookieName) {
  const raw = readCookie(cookieName);
  if (!raw) return null;

  try {
    return JSON.parse(decodeURIComponent(raw));
  } catch (e) {
    return null;
  }
}

function hasMarketingConsent(consent, keys) {
  if (!consent) return false;
  return keys.every((k) => consent[k] === '1' || consent[k] === 1 || consent[k] === true);
  //return keys.some((k) => consent[k] === '1' || consent[k] === 1 || consent[k] === true);
}

function isTargetPage(bodyClass) {
  return document.body && document.body.classList.contains(bodyClass);
}

function normalizeSrc(src) {
  if (!src) return '';
  return src.startsWith('//') ? `${window.location.protocol}${src}` : src;
}

function injectPaycorScript({ scriptId, src }) {
  if (document.getElementById(scriptId)) return;

  const s = document.createElement('script');
  s.id = scriptId;
  s.type = 'text/javascript';
  s.async = true;
  s.src = normalizeSrc(src);

  // Head is fine for async vendor scripts
  (document.head || document.body).appendChild(s);
}

function getContainer(selector) {
  return document.querySelector(selector);
}

function isLikelyPaycorIframe(iframe, iframeSrcMatch) {
  if (!iframe || iframe.tagName !== 'IFRAME') return false;

  const src = (iframe.getAttribute('src') || '').toLowerCase();
  if (src && src.includes(iframeSrcMatch.toLowerCase())) return true;

  // Heuristic fallback: if src is blank/about:blank, vendors often set name/id/class.
  const id = (iframe.id || '').toLowerCase();
  const name = (iframe.name || '').toLowerCase();
  const cls = (iframe.className || '').toLowerCase();

  // These are generic heuristics; keep them broad but not insane.
  if (id.includes('newton') || name.includes('newton') || cls.includes('newton')) return true;
  if (id.includes('gnewton') || name.includes('gnewton') || cls.includes('gnewton')) return true;

  return false;
}

function relocateIframe(iframe, container) {
  if (!iframe || !container) return false;
  if (iframe.parentElement === container) return true;

  container.appendChild(iframe);
  return true;
}

function observeForPaycorIframe({ containerSelector, iframeSrcMatch, observerTimeoutMs }) {
  let cachedIframe = null;

  // 1) If iframe already exists, try to relocate immediately
  const existingIframes = Array.from(document.querySelectorAll('iframe'));
  cachedIframe = existingIframes.find((f) => isLikelyPaycorIframe(f, iframeSrcMatch)) || null;

  const containerNow = getContainer(containerSelector);
  if (cachedIframe && containerNow) {
    relocateIframe(cachedIframe, containerNow);
    return;
  }

  // 2) Observe DOM for newly added iframes (or wrappers containing them)
  const observer = new MutationObserver((mutations) => {
    // Refresh container in case it was rendered later
    const container = getContainer(containerSelector);

    for (const m of mutations) {
      for (const node of m.addedNodes) {
        if (!(node instanceof Element)) continue;

        // Direct iframe added
        if (node.tagName === 'IFRAME' && isLikelyPaycorIframe(node, iframeSrcMatch)) {
          cachedIframe = node;
        }

        // Or iframe inside a wrapper
        if (!cachedIframe) {
          const inner = node.querySelector && node.querySelector('iframe');
          if (inner && isLikelyPaycorIframe(inner, iframeSrcMatch)) {
            cachedIframe = inner;
          }
        }

        // If we have both iframe + container, relocate and stop
        if (cachedIframe && container) {
          relocateIframe(cachedIframe, container);
          observer.disconnect();
          return;
        }
      }
    }
  });

  observer.observe(document.documentElement, { childList: true, subtree: true });

  // Safety stop
  window.setTimeout(() => observer.disconnect(), observerTimeoutMs);
}

export function initPaycorMooveEmbed(options = {}) {
  const cfg = { ...DEFAULTS, ...options };

  if (!isTargetPage(cfg.bodyClass)) return;

  const tryLoad = () => {
    const consent = getMooveConsent(cfg.cookieName);
    
    if (!hasMarketingConsent(consent, cfg.marketingKeys)) return;

    injectPaycorScript({ scriptId: cfg.scriptId, src: cfg.src });

    observeForPaycorIframe({
      containerSelector: cfg.containerSelector,
      iframeSrcMatch: cfg.iframeSrcMatch,
      observerTimeoutMs: cfg.observerTimeoutMs,
    });
  };

  // Returning visitors
  tryLoad();

  // Moove consent changes
  document.addEventListener('moove_gdpr_cookie_consent_changed', tryLoad);
}

